// 📄 services/guiasIntegracionService.ts
//
// "Guía de Integración Estándar" (2026-08-26, a pedido del usuario) —
// catálogo global de checklists de configuración, uno por combinación
// (solución Ariba, erpTipo) — ej. "SLP + S/4HANA Cloud Pública". Mismo
// criterio que catalogoBadisService.ts/catalogoApisService.ts: CRUD
// completo solo desde el panel Admin, lectura abierta a cualquier
// consultor autenticado (ver firestore.rules, colección `guiasIntegracion`).
//
// A diferencia de esos catálogos (id generado al azar por el llamador),
// el id de una guía es DETERMINÍSTICO — `${solucion}__${erpTipo}` — para
// que solo pueda existir UNA guía por combinación, y para que el lado del
// proyecto (GuiaIntegracionCard.tsx) pueda resolverla con un getDoc directo
// en vez de una query filtrada.
import { doc, collection, getDoc, getDocs, setDoc, deleteDoc, updateDoc, deleteField, serverTimestamp } from "firebase/firestore";
import { db } from '../firebase';

export interface PasoGuia {
  id: string;
  titulo: string;
  descripcion: string;
}

export interface GuiaIntegracionData {
  solucion: string;
  erpTipo: string;
  nombre: string;
  descripcion: string;
  pasos: PasoGuia[];
}

export interface GuiaIntegracion extends GuiaIntegracionData {
  id: string;
  createdAt?: any;
  updatedAt?: any;
}

const CATALOGO_COLLECTION = "guiasIntegracion";

export const guiaId = (solucion: string, erpTipo: string): string => `${solucion}__${erpTipo}`;

export const generarPasoId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const mapDoc = (id: string, data: any): GuiaIntegracion => ({
  id,
  solucion: data.solucion || '',
  erpTipo: data.erpTipo || '',
  nombre: data.nombre || '',
  descripcion: data.descripcion || '',
  pasos: Array.isArray(data.pasos) ? data.pasos : [],
  createdAt: data.createdAt,
  updatedAt: data.updatedAt
});

/** Todo el catálogo — para el panel Admin y el Ariba Toolbox (agrupado por solución). */
export const getGuiasIntegracion = async (): Promise<GuiaIntegracion[]> => {
  const snap = await getDocs(collection(db, CATALOGO_COLLECTION));
  return snap.docs.map((d) => mapDoc(d.id, d.data()));
};

/** LA guía de un proyecto puntual — null si el Admin todavía no cargó una para esta combinación. */
export const getGuiaIntegracion = async (solucion: string, erpTipo: string): Promise<GuiaIntegracion | null> => {
  if (!solucion || !erpTipo) return null;
  const snap = await getDoc(doc(db, CATALOGO_COLLECTION, guiaId(solucion, erpTipo)));
  if (!snap.exists()) return null;
  return mapDoc(snap.id, snap.data());
};

/**
 * Crea o reemplaza la guía de una combinación (solucion, erpTipo) — el id
 * es determinístico, así que solución/ERP NO se pueden editar después de
 * creada (el form del Admin los deja fijos al editar; para "moverla" a
 * otra combinación hay que borrar y crear de nuevo).
 */
export const saveGuiaIntegracion = async (
  solucion: string,
  erpTipo: string,
  data: { nombre: string; descripcion: string; pasos: PasoGuia[] },
  esNueva: boolean
): Promise<void> => {
  if (!solucion || !erpTipo) throw new Error("Elegí una Solución y un Tipo de ERP.");
  if (!data.nombre.trim()) throw new Error("El nombre de la guía es obligatorio.");
  const pasos = data.pasos.filter((p) => p.titulo.trim());
  if (pasos.length === 0) throw new Error("Agregá al menos un paso con título.");

  const ref = doc(db, CATALOGO_COLLECTION, guiaId(solucion, erpTipo));
  // Al crear (no editar), el id es determinístico por combinación — si ya
  // existe una guía para esta Solución+ERP, `setDoc` la pisaría entera sin
  // avisar. Se chequea acá para que el Admin edite la existente en vez de
  // perderla por accidente al intentar crear "otra" para la misma combinación.
  if (esNueva) {
    const existente = await getDoc(ref);
    if (existente.exists()) throw new Error("Ya existe una guía para esta combinación de Solución + ERP — editala en vez de crear una nueva.");
  }

  const payload: Record<string, any> = {
    solucion,
    erpTipo,
    nombre: data.nombre.trim(),
    descripcion: data.descripcion.trim(),
    pasos: pasos.map((p) => ({ id: p.id, titulo: p.titulo.trim(), descripcion: p.descripcion.trim() })),
    updatedAt: serverTimestamp()
  };
  if (esNueva) payload.createdAt = serverTimestamp();

  await setDoc(ref, payload);
};

export const deleteGuiaIntegracion = async (solucion: string, erpTipo: string): Promise<void> => {
  await deleteDoc(doc(db, CATALOGO_COLLECTION, guiaId(solucion, erpTipo)));
};

// ── Progreso por proyecto ────────────────────────────────────────────────
// Vive en projects/{id}.guiaIntegracionProgreso.{solucion}.{pasoId} = true
// (ver el campo declarado en projectService.ts) — namespaceado por
// solución porque un proyecto tiene un único erpTipo, así que ya alcanza
// para identificar a qué guía pertenece cada paso marcado. Mismo criterio
// "progreso namespaceado" que solucionIntegrationService.ts, pero sin
// ambiente/canal de por medio (es un checklist plano de configuración, no
// un formulario por paso).
export const setPasoGuiaCompletado = async (
  projectId: string,
  solucion: string,
  pasoId: string,
  completado: boolean
): Promise<void> => {
  const path = `guiaIntegracionProgreso.${solucion}.${pasoId}`;
  await updateDoc(doc(db, "projects", projectId), { [path]: completado ? true : deleteField() });
};
